import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { clsx } from 'clsx'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { ru } from 'date-fns/locale'

export interface DatePickerProps {
  value: Date | null
  onChange: (date: Date | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const POPUP_WIDTH = 280

interface PopupCoords {
  top: number
  left: number
  width: number
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'дд.мм.гггг',
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [viewMonth, setViewMonth] = useState<Date>(value ?? new Date())
  const [coords, setCoords] = useState<PopupCoords | null>(null)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)

  const updatePosition = useCallback(() => {
    const el = wrapperRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const width = Math.max(rect.width, POPUP_WIDTH)
    const maxLeft = window.innerWidth - width - 8
    setCoords({
      top: rect.bottom + 4,
      left: Math.max(8, Math.min(rect.left, maxLeft)),
      width,
    })
  }, [])

  const open = () => {
    if (disabled) return
    setViewMonth(value ?? new Date())
    setIsOpen(true)
  }

  useEffect(() => {
    if (!isOpen) return

    updatePosition()

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (wrapperRef.current?.contains(target) || popupRef.current?.contains(target)) return
      setIsOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen, updatePosition])

  const monthStart = startOfMonth(viewMonth)
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 })
  const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 })
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd })

  const rawMonthLabel = format(viewMonth, 'LLLL yyyy', { locale: ru })
  const monthLabel = rawMonthLabel.charAt(0).toUpperCase() + rawMonthLabel.slice(1)

  const handleSelect = (day: Date) => {
    onChange(day)
    setIsOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setIsOpen(false)
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        readOnly
        disabled={disabled}
        value={value ? format(value, 'dd.MM.yyyy', { locale: ru }) : ''}
        placeholder={placeholder}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        className={clsx(
          'admin-input cursor-pointer',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      />

      {isOpen && coords &&
        createPortal(
          <div
            ref={popupRef}
            className="fixed z-50 rounded-md border border-gray-200 bg-white p-3 shadow-lg"
            style={{ top: coords.top, left: coords.left, width: coords.width }}
          >
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setViewMonth(subMonths(viewMonth, 1))}
                className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                aria-label="Предыдущий месяц"
              >
                ‹
              </button>
              <div className="text-sm font-medium text-gray-900">{monthLabel}</div>
              <button
                type="button"
                onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                aria-label="Следующий месяц"
              >
                ›
              </button>
            </div>

            <div className="mb-1 grid grid-cols-7">
              {WEEKDAYS.map((weekday) => (
                <div
                  key={weekday}
                  className="flex h-8 w-8 items-center justify-center text-xs font-medium text-gray-400"
                >
                  {weekday}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {days.map((day) => {
                const selected = value ? isSameDay(day, value) : false
                const outside = !isSameMonth(day, viewMonth)
                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    onClick={() => handleSelect(day)}
                    className={clsx(
                      'flex h-8 w-8 items-center justify-center rounded-md text-sm',
                      outside ? 'text-gray-300' : 'text-gray-700',
                      selected
                        ? 'bg-gray-900 text-white hover:bg-gray-800'
                        : 'hover:bg-gray-100',
                      !selected && isToday(day) && 'ring-1 ring-inset ring-gray-400'
                    )}
                  >
                    {format(day, 'd', { locale: ru })}
                  </button>
                )
              })}
            </div>

            <div className="mt-2 border-t border-gray-100 pt-2 text-right">
              <button
                type="button"
                onClick={handleClear}
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              >
                Очистить
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
